import React, { useEffect } from 'react'

import { COLORS, customToast, PRODUCT_SELECTION_TYPE, SINGLE_SELECTION } from '@common'
import ProductSelectionSelector from '../ProductSelectionSelector'
import SelectionQuantityControl from './SelectionQuantityControl'

const SelectionBlock = React.memo(({ selection, value, onChange, options, onOpen }) => {
    const minSelection =
        selection.type === PRODUCT_SELECTION_TYPE.MINIMUM ? 2
        : selection.type === PRODUCT_SELECTION_TYPE.TAKEAWAY ? 1
        : 0
    // Handle both array and single object formats
    const valueLength =
        Array.isArray(value) ? value.length
        : value ? 1
        : 0
    const isSelectedMinimum = valueLength >= minSelection
    const isRequired = selection.isRequired
    const selectionName = selection.name ?? ''
    const textSelection =
        selection.type === PRODUCT_SELECTION_TYPE.SINGLE ? `${selectionName} (Optional)`
        : selection.type === PRODUCT_SELECTION_TYPE.MINIMUM ? 'Select your options (minimum 2)'
        : selection.type === PRODUCT_SELECTION_TYPE.TAKEAWAY ? selectionName
        : selection.type === PRODUCT_SELECTION_TYPE.NORMAL ? selectionName
        : `${selectionName} (Optional)`

    useEffect(() => {
        // if selection type is takeaway or normal, set the first active option as the value
        if ([PRODUCT_SELECTION_TYPE.TAKEAWAY, PRODUCT_SELECTION_TYPE.NORMAL].includes(selection.type)) {
            // Only set value if options exist and value hasn't been set yet
            if (options && options.length > 0 && (!value || (Array.isArray(value) && value.length === 0))) {
                const activeOption = options.find(o => !o.isDisabled)
                const selectedOption = activeOption || options[0]
                const isSingle = SINGLE_SELECTION.includes(selection.type) || selection.quantity === 1
                // For single selection, pass single object; for multi, pass array
                onChange(selection.id, isSingle ? selectedOption : [selectedOption])
            }
        }

        // eslint-disable-next-line
    }, [selection, options])

    const _onChange = val => {
        const isSingle = SINGLE_SELECTION.includes(selection.type) || selection.quantity === 1
        // For multi selection, check quantity limit
        // When isSingle is true, val is an object, not an array
        if (!isSingle && selection.quantity > 1 && Array.isArray(val) && val.length > selection.quantity) {
            customToast.plain(`This selection is limited to ${selection.quantity}`, {
                duration: 5000,
                icon: <i className="bx bx-sm bx-error text-danger"></i>,
            })
        } else {
            onChange(selection.id, val)
        }
    }

    const _renderQuantityControl = () => {
        if (!value) return null
        const isSingle = SINGLE_SELECTION.includes(selection.type) || selection.quantity === 1

        if (isSingle) {
            // For single selection, value should be a single object
            if (!value?.maxQuantity) return null
            return (
                <SelectionQuantityControl
                    label={value.label}
                    quantity={value.quantity || 1}
                    maxQuantity={value.maxQuantity}
                    onQuantityChange={newQuantity => {
                        const updatedValue = { ...value, quantity: newQuantity }
                        // react-select with isMulti=false expects a single object, not an array
                        onChange(selection.id, updatedValue)
                    }}
                />
            )
        } else {
            // For multi selection, value should already be an array
            const targetList = value?.filter?.(item => item.maxQuantity > 1 || item.maxQuantity !== null)
            if (!targetList?.length) return null

            return targetList.map(item => (
                <SelectionQuantityControl
                    key={item.value}
                    // className="mt-3"
                    label={item.label}
                    quantity={item.quantity || 1}
                    maxQuantity={item.maxQuantity}
                    onQuantityChange={newQuantity => {
                        onChange(
                            selection.id,
                            value.map(val => (val.value === item.value ? { ...val, quantity: newQuantity } : val))
                        )
                    }}
                />
            ))
        }
    }

    return (
        <div key={selection.id} className="selection-container">
            <div className="selection-header">
                <h6>
                    {textSelection}
                    {isRequired && <span className="required-mark">*</span>}
                </h6>
                {isRequired && isSelectedMinimum && (
                    <i className="bx bx-check-circle" style={{ color: COLORS.PRIMARY, fontSize: '20px' }}></i>
                )}
            </div>
            <ProductSelectionSelector
                value={value}
                onChange={_onChange}
                options={options}
                type={selection.type}
                quantity={selection.quantity}
                onOpen={onOpen}
            />
            <div className="mt-3 ">{_renderQuantityControl()}</div>
        </div>
    )
})

export default SelectionBlock
